/**
 * Locale-pinned date formatting — PLAT-UTILS-1 R3.
 *
 * This product signs documents with dates on them; two valuators looking
 * at the same value must see the same date string, not one shaped by
 * whatever OS locale their browser reports. Every formatter here pins
 * `'en-US'` explicitly instead of passing `undefined` to
 * `toLocaleDateString` (the prior pattern, and the source of the
 * cross-browser drift this module exists to remove).
 */

const LOCALE = 'en-US'

function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null
  // VAL-POLISH-1 item 2: a bare 'YYYY-MM-DD' (a date with no time component,
  // e.g. effective_date) parses as UTC midnight via `new Date(string)`, which
  // renders as the prior day in any negative-UTC-offset timezone. Construct
  // it from local components instead. Full ISO timestamps (every other
  // caller) still fall through to the line below, unchanged.
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, day] = value.split('-').map(Number)
    return new Date(y, m - 1, day)
  }
  const d = value instanceof Date ? value : new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Numeric date, e.g. "9/7/2026" — replaces a bare `toLocaleDateString()`. */
export function formatDate(value: string | number | Date | null | undefined): string {
  const d = toDate(value)
  return d ? d.toLocaleDateString(LOCALE) : ''
}

/**
 * Day + short month, e.g. "Sep 7" — replaces
 * `toLocaleDateString(undefined, { day: 'numeric', month: 'short' })`.
 */
export function formatShortDate(value: string | number | Date | null | undefined): string {
  const d = toDate(value)
  return d ? d.toLocaleDateString(LOCALE, { day: 'numeric', month: 'short' }) : ''
}

/**
 * Short month + day + year, e.g. "Sep 7, 2026" — PLAT-UTILS-3A. Replaces
 * `toLocaleDateString(..., { month: 'short', day: 'numeric', year: 'numeric' })`,
 * the shape repeated across the admin console (Users, Access Requests, Brand
 * Registrations, entitlements, pending-requests).
 */
export function formatMediumDate(value: string | number | Date | null | undefined): string {
  const d = toDate(value)
  return d ? d.toLocaleDateString(LOCALE, { month: 'short', day: 'numeric', year: 'numeric' }) : ''
}

/**
 * Long month + day + year, e.g. "September 7, 2026" — PLAT-UTILS-3A. Replaces
 * `toLocaleDateString(..., { year: 'numeric', month: 'long', day: 'numeric' })`.
 */
export function formatLongDate(value: string | number | Date | null | undefined): string {
  const d = toDate(value)
  return d ? d.toLocaleDateString(LOCALE, { year: 'numeric', month: 'long', day: 'numeric' }) : ''
}

/**
 * VAL-PATH-FOOTER (P2a). Time of day only, "3:45 PM" — the footer's save chip.
 * Newer ICU puts a narrow no-break space before AM/PM; it is shown as a plain one.
 */
export function formatTime(value: string | number | Date | null | undefined): string {
  const d = toDate(value)
  return d ? d.toLocaleTimeString(LOCALE, { hour: 'numeric', minute: '2-digit' }).replace(/\u202f/g, ' ') : ''
}

/**
 * Short month + day + hour:minute:second, e.g. "Sep 7, 3:45:12 PM" —
 * PLAT-UTILS-3A. Replaces `toLocaleString(..., { month: 'short', day:
 * 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' })`, used
 * by the operator audit-log feed (MonitorPage) where seconds-precision
 * matters for reading event ordering.
 */
export function formatDateTime(value: string | number | Date | null | undefined): string {
  const d = toDate(value)
  return d
    ? d.toLocaleString(LOCALE, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
      })
    : ''
}

/**
 * WALKFIX-C. Owner rule: every time the product shows is Central time,
 * whatever zone the browser is in. Server stamps are UTC and often carry no
 * zone ('2026-10-25T04:53:00'); `new Date` would read those as browser-local,
 * so a stamp with no Z or offset is read as UTC here.
 */
const CENTRAL = 'America/Chicago'

function toUtcDate(value: string | number | Date | null | undefined): Date | null {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T[\d:.]+$/.test(value)) {
    return toDate(`${value}Z`)
  }
  return toDate(value)
}

/** Central time of day, "11:53 PM" — the save chips. */
export function formatTimeCentral(value: string | number | Date | null | undefined): string {
  const d = toUtcDate(value)
  return d
    ? d.toLocaleTimeString(LOCALE, { hour: 'numeric', minute: '2-digit', timeZone: CENTRAL }).replace(/ /g, ' ')
    : ''
}

/** Central date and time, "Oct 24, 2026, 11:58 PM" — who-did-what-when lines. */
export function formatDateTimeCentral(value: string | number | Date | null | undefined): string {
  const d = toUtcDate(value)
  return d
    ? d
        .toLocaleString(LOCALE, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          timeZone: CENTRAL,
        })
        .replace(/ /g, ' ')
    : ''
}
